import React, { useContext, useState } from "react";
import { useModal } from "react-modal-hook";
import { fontWeights } from "common/utils/styles";
import {
  GetWorkflowQuery,
  useDeleteWorkflowStepMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { spacing } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { CommonModal } from "../Common/CommonModal";
import Modal from "../Common/Modal";
import { Body } from "../Common/Typography";
import Checkbox from "../Inputs/Checkbox";

type DeleteWorkflowStepModalProps = {
  workflowStep: NonNullable<
    GetWorkflowQuery["workflow"]
  >["workflowSteps"][number];
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
  onCancel: () => void;
  onSuccess: () => void;
};

export const DeleteWorkflowStepModal = ({
  workflowStep,
  workflow,
  onSuccess,
  onCancel,
}: DeleteWorkflowStepModalProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { user, admin } = useContext(AuthContext);

  const [dataDeletedConfirmed, setDataDeletedConfirmed] = useState(false);
  const [tasksDeletedConfirmed, setTasksDeletedConfirmed] = useState(false);
  const [collaboratorsRemovedConfirmed, setCollaboratorsRemovedConfirmed] =
    useState(false);

  const allConfirmed =
    dataDeletedConfirmed &&
    tasksDeletedConfirmed &&
    collaboratorsRemovedConfirmed;

  const [deleteWorkflowStepMutation, { loading }] =
    useDeleteWorkflowStepMutation({
      onCompleted: () => {
        addSuccessToast("Your step was successfully deleted");
        onSuccess();
        track("Workflow step deleted", {
          workflowId: workflow.id,
          workflowStepId: workflowStep.id,
          workflowDefinitionId: workflow.workflowDefinition.id,
          workflowDefinitionName: workflow.workflowDefinition.name,
          userId: user?.id ?? admin?.id,
          userEmail: user?.email ?? admin?.email,
        });
      },
      onError: () => {
        addErrorToast(
          "There was an error deleting this step. Please try again. If the problem persists, please email support@withforerunner.com",
        );
      },
    });

  const onSubmit = async () => {
    await deleteWorkflowStepMutation({
      variables: {
        workflowStepId: workflowStep.id,
      },
    });
  };

  const content = (
    <FlexColumn style={{ gap: spacing.m.value }}>
      <Body size="default" type="regular">
        By removing this step, you confirm that:
      </Body>
      <FlexColumn style={{ gap: spacing.s.value }}>
        <Checkbox
          label="All data and uploaded files associated with the record will be deleted. *"
          value={dataDeletedConfirmed}
          onChange={setDataDeletedConfirmed}
        />
        <Checkbox
          label="Any tasks associated with the record will be deleted. *"
          value={tasksDeletedConfirmed}
          onChange={setTasksDeletedConfirmed}
        />
        <Checkbox
          label="This step will no longer be accessible to the people you've shared it with. *"
          value={collaboratorsRemovedConfirmed}
          onChange={setCollaboratorsRemovedConfirmed}
        />
      </FlexColumn>
      <Body size="default" type="regular">
        Changes will only affect this workflow. This action{" "}
        <span style={{ fontWeight: fontWeights.bold }}>cannot</span> be undone.
      </Body>
    </FlexColumn>
  );

  return (
    <CommonModal
      title="Remove step"
      subtitle={workflowStep.name}
      content={content}
      onSubmit={onSubmit}
      onCancel={onCancel}
      loading={loading}
      disabled={!allConfirmed || loading}
      primaryButtonText="Delete"
      variant="destructive"
      size="medium"
      dataTestId="delete-workflow-step-modal"
    />
  );
};

export const useDeleteWorkflowStepModal = ({
  workflowStep,
  workflow,
}: {
  workflowStep: NonNullable<
    GetWorkflowQuery["workflow"]
  >["workflowSteps"][number];
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
}) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <DeleteWorkflowStepModal
          workflowStep={workflowStep}
          workflow={workflow}
          onCancel={hideModal}
          onSuccess={hideModal}
        />
      </Modal>
    ),
    [workflowStep, workflow],
  );

  return [showModal, hideModal] as const;
};
